/**
 * Oda Nobunaga — „Sun's Origin" (súťaž dizajnérov Restoration Games).
 *
 * Postava stojí na **obkľúčení**: súperova figúrka je obkľúčená, keď s ňou
 * susedia aspoň dve figúrky hráča (poznámka k balíčku v zdroji). A na
 * schopnosti MASTER STRATEGIST — ostatné vlastné figúrky v Odovej zóne majú
 * +1 k zahraným kartám súboja.
 */

import { shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, drawCard, gainAction, getMap, healFighter, heroOf } from '../ops.js';
import type { FighterId, PlayerIndex } from '../types.js';
import { feint, momentousShift } from './common.js';
import { adjacentFighters, askMove, enemyFighters, fighterOrNull, isFlanked } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

function opponentFlanked(e: EffectCtx): boolean {
  return isFlanked(e.ctx, e.opponentFighterId, e.player);
}

/** „Draw a number of cards equal to the amount of combat damage dealt to the opposing fighter." */
const studentOfWar: CardEffect = {
  after: (e) => {
    for (let i = 0; i < (e.damage ?? 0); i++) drawCard(e.ctx, e.player);
  },
};

/** „If the opposing fighter is flanked, this card's value is 5 instead." */
const fireAndFlames: CardEffect = {
  during: (e, value) => (opponentFlanked(e) ? 5 : value),
};

/**
 * „IMMEDIATELY: If the opposing fighter is flanked, cancel all effects on your
 * opponent's card. AFTER COMBAT: Move your fighter up to 2 spaces."
 */
const lightningAndThunder: CardEffect = {
  cancelsOpponentIf: opponentFlanked,
  after: (e) => {
    if (!e.fighterId) return;
    askMove(e, { fighters: [e.fighterId], max: 2, label: 'Presuň figúrku až o 2 priestory' });
  },
};

/** „If the opposing fighter is flanked, ignore the value of your opponent's card." */
const sunAndMoon: CardEffect = {
  // Nie je to voľba, ale musí sa rozhodnúť pred výpočtom hodnôt — `duringChoice`
  // je presne ten okamih.
  duringChoice: (e) => {
    if (opponentFlanked(e)) e.ctx.state.combat?.flags?.push(e.role === 'attacker' ? 'zeroDefense' : 'zeroAttack');
  },
};

/** „Draw 1 card. Then, move each of your fighters up to 2 spaces." */
const battleManeuvers: CardEffect = {
  after: (e) => {
    drawCard(e.ctx, e.player);
    for (const fighter of e.ctx.state.fighters.filter((f) => f.owner === e.player)) {
      askMove(e, { fighters: [fighter.id], max: 2, label: `Presuň ${fighter.name} až o 2 priestory` });
    }
  },
};

/** „If the opposing fighter is flanked, your fighter recovers 2 health." */
const patienceAndStrategy: CardEffect = {
  after: (e) => {
    if (e.fighterId && opponentFlanked(e)) healFighter(e.ctx, e.fighterId, 2);
  },
};

/**
 * „IMMEDIATELY: Your fighter may swap spaces with an adjacent friendly fighter.
 * If they do, the other fighter is now the defender. AFTER COMBAT: Deal 1
 * damage to the opposing fighter."
 */
const springTheTrap: CardEffect = {
  immediate: (e) => {
    const fighter = fighterOrNull(e.ctx, e.fighterId);
    if (!fighter) return;
    const friends = adjacentFighters(e.ctx, fighter.spaceId).filter(
      (id) => fighterOrNull(e.ctx, id)?.owner === e.player,
    );
    if (friends.length === 0) return;
    askChoice(e.ctx, {
      kind: 'swap',
      player: e.player,
      source: e.cardId,
      label: 'Spring the Trap: vymeniť miesto so susednou vlastnou figúrkou? Obrancom bude ona.',
      optional: true,
      fighterId: fighter.id,
      candidates: friends,
    });
  },
  after: (e) => {
    if (e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 1);
  },
};

const REINFORCE = {
  heal: 'každá vlastná figúrka +1 zdravie',
  draw: 'potiahni 2 karty',
  action: 'získaj 1 akciu',
} as const;
type ReinforcePick = keyof typeof REINFORCE;

function reinforceOptions(except: ReinforcePick | null) {
  return (Object.keys(REINFORCE) as ReinforcePick[])
    .filter((key) => key !== except)
    .map((key) => ({
      label: REINFORCE[key],
      follow: { kind: 'effect' as const, effect: 'oda-nobunaga.reinforce', data: `${key}:${except ? 'last' : 'first'}` },
    }));
}

/** „Choose 2 different effects: each friendly fighter recovers 1 health; draw 2 cards; gain 1 action." */
const reinforce: CardEffect = {
  scheme: (e) => {
    e.ctx.state.pending.push({
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'Reinforce: vyber prvý z dvoch efektov',
      optional: false,
      options: reinforceOptions(null),
    });
  },
  onFollow: (ctx, choice, data) => {
    const [pick, round] = data.split(':') as [ReinforcePick, 'first' | 'last'];
    if (pick === 'heal') {
      for (const fighter of ctx.state.fighters.filter((f) => f.owner === choice.player)) {
        healFighter(ctx, fighter.id, 1);
      }
    } else if (pick === 'draw') {
      drawCard(ctx, choice.player);
      drawCard(ctx, choice.player);
    } else {
      gainAction(ctx, choice.player);
    }
    if (round === 'first') {
      ctx.state.pending.unshift({
        kind: 'option',
        player: choice.player,
        source: choice.source,
        label: 'Reinforce: vyber druhý, iný efekt',
        optional: false,
        options: reinforceOptions(pick),
      });
    }
  },
};

/** „Deal 2 damage to each opposing flanked fighter." */
const demonKing: CardEffect = {
  scheme: (e) => {
    for (const id of enemyFighters(e.ctx, e.player)) {
      if (isFlanked(e.ctx, id, e.player)) dealDamage(e.ctx, id, 2);
    }
  },
};

export const odaCards: EffectRegistry = {
  'oda-nobunaga.student-of-war': studentOfWar,
  'oda-nobunaga.fire-and-flames': fireAndFlames,
  'oda-nobunaga.lightning-and-thunder': lightningAndThunder,
  'oda-nobunaga.sun-and-moon': sunAndMoon,
  'oda-nobunaga.battle-maneuvers': battleManeuvers,
  'oda-nobunaga.momentous-shift': momentousShift,
  'oda-nobunaga.patience-and-strategy': patienceAndStrategy,
  'oda-nobunaga.spring-the-trap': springTheTrap,
  'oda-nobunaga.reinforce': reinforce,
  'oda-nobunaga.demon-king-of-the-sixth-heaven': demonKing,
  'oda-nobunaga.pragmatism': feint,
};

/** Ostatná vlastná figúrka v Odovej zóne dostane +1 (Oda sám nie). */
function strategistBonus(ctx: Ctx, player: PlayerIndex, fighterId: FighterId | null): number {
  const oda = heroOf(ctx.state, player);
  const fighter = fighterOrNull(ctx, fighterId);
  if (!oda || !fighter || fighter.id === oda.id || fighter.owner !== player) return 0;
  return shareZone(getMap(ctx), oda.spaceId, fighter.spaceId) ? 1 : 0;
}

/**
 * MASTER STRATEGIST — „Other friendly fighters in Oda Nobunaga's zone add +1 to
 * the value of their played combat cards."
 */
export const odaSpecial: HeroSpecial = {
  name: 'Master Strategist',
  attackModifier: strategistBonus,
  defenseModifier: strategistBonus,
};
